import { HTMLAttributes } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import clsx from "clsx";
import { theme } from "$utils/theme/bataillog";
import TextType from "./TextType";

type Props = {
  type?: `${TextType}`; // Cast enum to type: https://stackoverflow.com/a/75722379/16939610
};

function Text({
  type = "text",
  className = "",
  children,
  ...anyProps
}: Props & HTMLAttributes<any>) {
  const classes = useStyles();
  switch (type) {
    case "h1":
      return (
        <h1 className={clsx(classes.h1, className)} {...anyProps}>
          {children}
        </h1>
      );
    case "h2":
      return (
        <h2 className={clsx(classes.h2, className)} {...anyProps}>
          {children}
        </h2>
      );
    case "h3":
      return (
        <h3 className={clsx(classes.h3, className)} {...anyProps}>
          {children}
        </h3>
      );
    case "thead":
      return (
        <span className={clsx(classes.tHead, className)} {...anyProps}>
          {children}
        </span>
      );
    case "important":
      return (
        <span className={clsx(classes.important, className)} {...anyProps}>
          {children}
        </span>
      );
    case "date":
      return (
        <p className={clsx(classes.date, className)} {...anyProps}>
          {children}
        </p>
      );
    case "link":
      return (
        <span className={clsx(classes.link, className)} {...anyProps}>
          {children}
        </span>
      );
    case "secondary":
      return (
        <p className={clsx(classes.secondary, className)} {...anyProps}>
          {children}
        </p>
      );
    case "error":
      return (
        <span className={clsx(classes.error, className)} {...anyProps}>
          {children}
        </span>
      );
    case "disabled":
      return (
        <span className={clsx(classes.disabled, className)} {...anyProps}>
          {children}
        </span>
      );
    case "warning":
      return (
        <span className={clsx(classes.warning, className)} {...anyProps}>
          {children}
        </span>
      );
    default:
      return (
        <span className={clsx(classes.text, className)} {...anyProps}>
          {children}
        </span>
      );
  }
}

const useStyles = createUseStyles(() => ({
  bold: {
    fontWeight: 800,
  },
  semiBold: {
    fontWeight: 700,
  },
  regular: {
    fontWeight: "normal",
  },
  textTypes: {
    /* TODO en fixant cette erreur, je crée peut être plein d'impact
    fontFamily: theme.font?.workSans,
    color: theme.color.black, */
  },
  h1: {
    composes: "$textTypes $semiBold",
    color: theme.color.blueLight,
    fontSize: 46,
  },
  h2: {
    composes: "$textTypes $bold",
    fontWeight: 800,
    color: theme.color.blue,
    fontSize: 25,
  },
  h3: {
    composes: "$textTypes $regular",
    fontSize: 18,
  },
  tHead: {
    composes: "$textTypes $regular",
    fontSize: 14,
    textTransform: "capitalize",
    color: theme.color.gray5,
  },
  important: {
    composes: "$semiBold $textTypes",
    fontSize: 14,
    color: theme.color.blue,
  },
  error: {
    composes: "$semiBold $semiBold",
    fontSize: 14,
    color: theme.color.red,
  },
  text: {
    composes: "$textTypes $regular ",
    fontSize: 15,
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
  },
  date: {
    composes: "$textTypes $regular ",
    fontSize: 14,
    color: theme.color.gray5,
  },
  link: {
    composes: "$textTypes $regular ",
    fontSize: 15,
  },
  secondary: {
    composes: "$textTypes $regular ",
    fontSize: 12,
    color: theme.color.gray5,
  },
  disabled: {
    composes: "$textTypes $regular ",
    fontSize: 14,
    color: theme.color.gray5,
  },
  warning: {
    composes: "$semiBold $semiBold",
    fontSize: 14,
    color: theme.color.orange,
  },
}));

export default Text;
